import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Search, Edit, X, Trash2, Check } from "lucide-react";
import { toast } from "react-hot-toast";

interface PaymentType {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

const SettingsView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState<PaymentType | null>(null);
  const [newType, setNewType] = useState({ name: "", description: "" });

  // Mock data - gerçek API ile değiştirilecek
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([
    {
      id: "1",
      name: "Kayıt Ücreti",
      description: "Yeni öğrenci kayıt ücreti",
      isActive: true,
    },
    {
      id: "2",
      name: "Servis Ücreti",
      description: "Okul servisi aylık ücreti",
      isActive: true,
    },
    {
      id: "3",
      name: "Yemek Ücreti",
      description: "Okul yemeği aylık ücreti",
      isActive: true,
    },
    {
      id: "4",
      name: "Etkinlik Ücreti",
      description: "Okul etkinlikleri için ödeme",
      isActive: true,
    },
    {
      id: "5",
      name: "Kitap Ücreti",
      description: "Ders kitapları ve materyaller",
      isActive: true,
    },
  ]);

  const filteredTypes = paymentTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleCreate = () => {
    if (!newType.name.trim()) {
      toast.error("Tip adı zorunludur");
      return;
    }

    const newPaymentType: PaymentType = {
      id: String(Date.now()),
      name: newType.name,
      description: newType.description,
      isActive: true,
    };

    setPaymentTypes([...paymentTypes, newPaymentType]);
    setNewType({ name: "", description: "" });
    setShowCreateModal(false);
    toast.success("Ödeme tipi başarıyla oluşturuldu");
  };

  const handleEdit = (type: PaymentType) => {
    setEditingType(type);
    setShowEditModal(true);
  };

  const handleUpdate = () => {
    if (!editingType) return;

    setPaymentTypes(
      paymentTypes.map((t) => (t.id === editingType.id ? editingType : t)),
    );
    setShowEditModal(false);
    setEditingType(null);
    toast.success("Ödeme tipi başarıyla güncellendi");
  };

  const handleToggleActive = (id: string) => {
    setPaymentTypes(
      paymentTypes.map((t) =>
        t.id === id ? { ...t, isActive: !t.isActive } : t,
      ),
    );
    toast.success("Durum güncellendi");
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Bu ödeme tipini silmek istediğinizden emin misiniz?")) {
      setPaymentTypes(paymentTypes.filter((t) => t.id !== id));
      toast.success("Ödeme tipi başarıyla silindi");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Okul Ödeme Tipleri
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Ödeme tiplerini yönetin
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Yeni Tip Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Tip adı ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Ödeme Tipi
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTypes.map((type) => (
                  <tr
                    key={type.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {type.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {type.description}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(type)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(type.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            type.isActive
                              ? "text-red-600 hover:bg-red-50"
                              : "text-green-600 hover:bg-green-50"
                          }`}
                          title={
                            type.isActive ? "Devre Dışı Bırak" : "Aktif Et"
                          }
                        >
                          {type.isActive ? (
                            <X className="w-4 h-4" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTypes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Ödeme tipi bulunamadı</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Yeni Ödeme Tipi Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tip Adı <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={newType.name}
                  onChange={(e) =>
                    setNewType({ ...newType, name: e.target.value })
                  }
                  placeholder="Örn: Kayıt Ücreti"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <Input
                  type="text"
                  value={newType.description}
                  onChange={(e) =>
                    setNewType({ ...newType, description: e.target.value })
                  }
                  placeholder="Tip açıklaması"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewType({ name: "", description: "" });
                }}
              >
                İptal
              </Button>
              <Button
                onClick={handleCreate}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Oluştur
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ödeme Tipini Düzenle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tip Adı <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={editingType.name}
                  onChange={(e) =>
                    setEditingType({ ...editingType, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <Input
                  type="text"
                  value={editingType.description}
                  onChange={(e) =>
                    setEditingType({
                      ...editingType,
                      description: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingType(null);
                }}
              >
                İptal
              </Button>
              <Button
                onClick={handleUpdate}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Güncelle
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsView;
